import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { glossaryKeys } from '@/shared/lib/query/queryKeys';
import type { OwnerEditHook } from '@/shared/types/editHooks';
import type { UpdateOwnerItem } from '@/shared/types/owner';

import { useOwnerEdit } from '@/entities/owner';

import type { GlossaryTermDetail } from '@/features/glossary/api';

type UseGlossaryTermOwnerEditProps = {
  termId?: string;
  termFqn?: string;
};

export const useGlossaryTermOwnerEdit = ({ termId, termFqn }: UseGlossaryTermOwnerEditProps): OwnerEditHook => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => (termFqn ? glossaryKeys.termDetailByFqn(termFqn) : []), [termFqn]);

  const getOwnersFromData = useCallback((data: GlossaryTermDetail | undefined): UpdateOwnerItem[] => {
    if (!data) return [];
    return data.owners;
  }, []);

  const invalidateQuery = useCallback(() => {
    if (termFqn) {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.termDetailByFqn(termFqn) });
    }
  }, [queryClient, termFqn]);

  const ownerEdit = useOwnerEdit<GlossaryTermDetail>({
    resourceId: termId || '',
    apiPath: '/glossaryTerms',
    queryKey,
    getOwnersFromData,
    invalidateQuery,
  });

  if (!termId || !termFqn) {
    return {
      isOpen: false,
      activeTab: 'teams',
      selectedTeamId: null,
      selectedUserIds: [],
      teamOwners: [],
      userOwners: [],
      currentOwners: [],
      isLoadingTeams: false,
      isLoadingUsers: false,
      isUpdating: false,
      hasChanges: false,
      handleOpen: () => {},
      handleClose: () => {},
      setActiveTab: () => {},
      handleTeamSelect: () => {},
      handleUserSelect: () => {},
      handleUpdate: () => {},
    };
  }

  return ownerEdit;
};
